import { createRouter, createWebHistory } from 'vue-router'
import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'
import { useSessionStore } from '@/stores/session'
import { readCredentialsFromUrl } from '@/utils/token'

const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_BASE_URL),
  routes: [
    {
      path: '/',
      name: 'landing',
      component: () => import('@/pages/LandingPage.vue'),
      meta: { fullBleed: true },
    },
    {
      path: '/waiting-room',
      name: 'waitingRoom',
      component: () => import('@/pages/WaitingRoomPage.vue'),
      meta: { requiresToken: true, fullBleed: true },
    },
    {
      path: '/room',
      name: 'videoRoom',
      component: () => import('@/pages/VideoRoomPage.vue'),
      meta: { requiresToken: true, fullBleed: true },
    },
    {
      path: '/template-editor',
      name: 'templateEditor',
      component: () => import('@/pages/TemplateEditorPage.vue'),
      meta: { requiresToken: true, tokenPurpose: 'template-editor' },
    },
    {
      path: '/token-generator',
      name: 'tokenGenerator',
      component: () => import('@/pages/TokenGeneratorPage.vue'),
    },
    {
      path: '/playground',
      name: 'playground',
      component: () => import('@/pages/PlaygroundPage.vue'),
    },
  ],
})

/** The credentials the session is currently running on. */
export interface SessionCredentials {
  token: string | null
  patientName: string
}

/**
 * The same location with the join credentials attached, or null if it already
 * carries them (or there are none to attach).
 *
 * Every in-app navigation is a bare `router.push({ name })`, which would drop
 * the token at the first hop and leave a URL that cannot survive a refresh.
 * Re-attaching keeps the token in the address bar for the whole session, so a
 * reload anywhere resumes it.
 *
 * The fragment is the channel used, whichever one the link arrived on: browsers
 * never send a fragment to a server, so the token stays out of access logs, and
 * `pn` rides along in the one place it is allowed to travel.
 */
export function locationWithCredentials(
  to: RouteLocationNormalized,
  credentials: SessionCredentials,
): RouteLocationRaw | null {
  if (!credentials.token) return null
  if ('token' in to.query) return null
  if (new URLSearchParams(to.hash.replace(/^#/, '')).has('token')) return null

  const fragment = new URLSearchParams({ token: credentials.token })
  if (credentials.patientName) fragment.set('pn', credentials.patientName)

  return { path: to.path, query: to.query, hash: `#${fragment.toString()}` }
}

router.beforeEach(async (to) => {
  const sessionStore = useSessionStore()

  // If we have a token in the URL and haven't initialized yet, do so
  // (skip for playground — it handles its own session init). The token may be
  // in the fragment or the query; readCredentialsFromUrl knows both.
  const { token } = readCredentialsFromUrl()
  if (token && sessionStore.status === 'none' && to.name !== 'playground') {
    await sessionStore.initFromUrl()

    // Redirect template-editor tokens to the template editor page
    if (sessionStore.isTemplateEditor && to.name === 'landing') {
      return { name: 'templateEditor' }
    }
  }

  // Protect routes that require a valid token
  if (to.meta.requiresToken) {
    const validStatuses = ['valid', 'too_early']
    if (!validStatuses.includes(sessionStore.status)) {
      return { name: 'landing' }
    }

    // Enforce token purpose: template-editor tokens can only access template-editor routes
    const routePurpose = to.meta.tokenPurpose as string | undefined
    const tokenPurpose = sessionStore.payload?.purpose

    if (routePurpose && tokenPurpose !== routePurpose) {
      return { name: 'landing' }
    }
    if (!routePurpose && tokenPurpose === 'template-editor') {
      return { name: 'landing' }
    }
  }

  // Keep the token on the address bar for every location the session visits, so
  // a refresh anywhere resumes it. Runs after the checks above so a rejected
  // session is never handed credentials on its way out. The playground mints
  // and manages its own.
  if (to.name !== 'playground') {
    const carried = locationWithCredentials(to, {
      token: sessionStore.rawToken,
      patientName: sessionStore.patientNameFromHost,
    })
    if (carried) return carried
  }
})

export default router
